import { checkOtpMatch, resendOtp } from "./utils/api";
import {
  ArrowLongRightIcon,
  ArrowPathIcon,
  Button,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  KeyIcon,
  Link,
  Navigate,
  useEffect,
  useState,
} from "@";
import OtpInput from "react-otp-input";

const RESEND_SECONDS = 120;

const formatTime = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const CheckOtp = () => {
  if (!localStorage.getItem("phoneNumber")) {
    return <Navigate to="/forget-password" />;
  }

  const [isLoading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);

  // Tick the countdown down to zero, one second at a time. Re-runs whenever
  // secondsLeft changes (including the reset on a successful resend).
  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const phoneNumber = localStorage.getItem("phoneNumber");
  const maskedPhone = phoneNumber
    ? `${phoneNumber.slice(0, 3)}*****${phoneNumber.slice(-3)}`
    : "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await checkOtpMatch(otp);
    } finally {
      setLoading(false);
    }
  };

  const resendNewOtp = async () => {
    if (isLoading || secondsLeft > 0) return;
    setLoading(true);
    try {
      await resendOtp();
      setOtp("");
      setSecondsLeft(RESEND_SECONDS);
    } finally {
      setLoading(false);
    }
  };

  const canResend = secondsLeft <= 0 && !isLoading;

  return (
    <div className="relative p-6 md:p-8">
      {/* Step indicator */}
      <div className="mb-5 flex items-center gap-2">
        <span className="inline-flex h-7 items-center gap-1.5 rounded-full bg-purple-50 px-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-purple-700 ring-1 ring-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40">
          <KeyIcon className="h-3 w-3" />
          استعادة الحساب
        </span>
        <div className="flex items-center gap-1">
          <span className="h-1.5 w-2 rounded-full bg-purple-300 dark:bg-purple-600" />
          <span className="h-1.5 w-6 rounded-full bg-purple-700 dark:bg-purple-400" />
          <span className="h-1.5 w-2 rounded-full bg-gray-200 dark:bg-gray-700" />
        </div>
      </div>

      <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100 md:text-3xl">
        التحقق من الرمز
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
        أدخل رمز التحقق المرسل إلى{" "}
        <span
          dir="ltr"
          className="inline-flex items-center gap-1 font-mono font-bold tabular-nums text-purple-700 dark:text-purple-400"
        >
          <ChatBubbleLeftRightIcon className="h-4 w-4" />
          {maskedPhone}
        </span>
      </p>

      <form className="mt-7 space-y-5" onSubmit={handleSubmit}>
        {/* OTP Inputs */}
        <div className="rounded-2xl border border-gray-100 bg-gradient-to-l from-purple-50/30 via-white to-purple-50/20 p-4 dark:border-gray-700/60 dark:from-purple-950/15 dark:via-gray-800 dark:to-purple-950/10">
          <OtpInput
            value={otp}
            numInputs={4}
            onChange={setOtp}
            skipDefaultStyles={true}
            renderSeparator={
              <span className="hidden text-purple-300 dark:text-purple-700 sm:inline">
                ·
              </span>
            }
            inputStyle="!w-14 sm:!w-16 h-14 sm:h-16 rounded-2xl text-center text-2xl font-bold tabular-nums text-gray-900 dark:text-gray-100 bg-white border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none transition-all  dark:bg-transparent dark:border-gray-500 dark:focus:border-purple-500 dark:focus:ring-purple-900/40"
            renderInput={(props) => <input {...props} />}
            containerStyle="otp-container justify-between gap-2 sm:gap-3"
          />
        </div>

        <Button
          type="submit"
          isLoading={isLoading}
          title="تأكيد الرمز"
          className="!w-full rounded-full !py-3"
        />

        {/* Resend with countdown */}
        <div className="flex flex-col items-center gap-2.5">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            لم يصلك الرمز؟
          </p>
          <button
            type="button"
            onClick={resendNewOtp}
            disabled={!canResend}
            aria-label={canResend ? "إعادة إرسال الرمز" : "إعادة الإرسال متاحة بعد انتهاء العد"}
            className="group inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold tracking-tight text-purple-700 transition-all hover:border-purple-200 hover:bg-purple-50 disabled:cursor-not-allowed disabled:border-gray-100 disabled:text-gray-400 disabled:hover:border-gray-100 disabled:hover:bg-transparent dark:border-gray-700 dark:text-purple-400 dark:hover:border-purple-700/60 dark:hover:bg-purple-950/30 dark:disabled:border-gray-700/60 dark:disabled:text-gray-500"
          >
            {secondsLeft > 0 ? (
              <>
                <ClockIcon className="h-4 w-4" aria-hidden />
                <span>
                  إعادة الإرسال خلال{" "}
                  <span dir="ltr" className="font-mono tabular-nums">
                    {formatTime(secondsLeft)}
                  </span>
                </span>
              </>
            ) : (
              <>
                <ArrowPathIcon
                  className="h-4 w-4 transition-transform duration-500 group-hover:rotate-180"
                  aria-hidden
                />
                إعادة إرسال الرمز
              </>
            )}
          </button>
        </div>

        <div className="flex items-center justify-center gap-1 border-t border-gray-100 pt-5 dark:border-gray-700/60">
          <Link
            to="/login"
            className="group/link inline-flex items-center gap-1 text-xs font-semibold tracking-tight text-purple-700 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300"
          >
            تسجيل الدخول إلى حسابك
            <ArrowLongRightIcon className="h-3.5 w-3.5 transition-transform group-hover/link:-translate-x-0.5 rtl:rotate-180" />
          </Link>
        </div>
      </form>
    </div>
  );
};

export default CheckOtp;
